import React, { useState, useEffect } from 'react';
import Section from '../ui/Section';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Modal from '../ui/Modal';
import { getProjects } from '../../services/api';

const Portfolio = () => {
    const [projects, setProjects] = useState([]);
    const [selectedProject, setSelectedProject] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const data = await getProjects();
                setProjects(data);
            } catch (error) {
                console.error("Failed to fetch projects:", error);
                // Fallback or empty state could be handled here
            } finally {
                setLoading(false);
            }
        };

        fetchProjects();
    }, []);

    return (
        <Section id="projects" background="white">
            <div className="text-center max-w-3xl mx-auto mb-16">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 text-slate-900">Featured Projects</h2>
                <p className="text-slate-600 text-lg">
                    A showcase of our engineering excellence and commitment to quality across various sectors.
                </p>
            </div>

            {loading ? (
                <div className="text-center py-12">
                    <p className="text-slate-500">Loading projects...</p>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 gap-8">
                    {projects.map((project, index) => (
                        <Card
                            key={project.id || index}
                            className="group cursor-pointer"
                            onClick={() => setSelectedProject(project)}
                        >
                            <div className="relative h-64 overflow-hidden">
                                <img
                                    src={project.image}
                                    alt={project.title}
                                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                                />
                                <div className="absolute inset-0 bg-slate-900/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                                    <Button variant="outline" className="text-white border-white hover:bg-white hover:text-slate-900">
                                        View Details
                                    </Button>
                                </div>
                            </div>
                            <div className="p-6">
                                <span className="text-gold-600 font-semibold text-sm tracking-wider uppercase mb-2 block">
                                    {project.category}
                                </span>
                                <h3 className="text-2xl font-bold mb-3 text-slate-900 group-hover:text-gold-600 transition-colors">
                                    {project.title}
                                </h3>
                                <p className="text-slate-600 line-clamp-3">
                                    {project.description}
                                </p>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <div className="text-center mt-12">
                <Button variant="secondary">View All Projects</Button>
            </div>

            {/* Project Details Modal */}
            {selectedProject && (
                <Modal isOpen={!!selectedProject} onClose={() => setSelectedProject(null)}>
                    <div className="relative h-64 md:h-96">
                        <img
                            src={selectedProject.image}
                            alt={selectedProject.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-8">
                            <div>
                                <span className="text-gold-500 font-bold tracking-wider uppercase mb-2 block">
                                    {selectedProject.category}
                                </span>
                                <h3 className="text-3xl md:text-4xl font-bold text-white">
                                    {selectedProject.title}
                                </h3>
                            </div>
                        </div>
                    </div>
                    <div className="p-8">
                        <div className="grid md:grid-cols-3 gap-8">
                            <div className="md:col-span-2">
                                <h4 className="text-xl font-bold text-slate-900 mb-4">Project Overview</h4>
                                <p className="text-slate-600 text-lg leading-relaxed mb-6">
                                    {selectedProject.description}
                                </p>
                                <p className="text-slate-600 leading-relaxed">
                                    Detailed structural analysis and design were performed to ensure the highest safety standards and architectural integrity. Our team worked closely with architects and contractors to deliver this project on time and within budget.
                                </p>
                            </div>
                            <div className="bg-slate-50 p-6 rounded-lg h-fit">
                                <h4 className="text-lg font-bold text-slate-900 mb-4">Project Details</h4>
                                <ul className="space-y-3 text-sm">
                                    <li className="flex justify-between border-b border-slate-200 pb-2">
                                        <span className="text-slate-500">Location</span>
                                        <span className="text-slate-900 font-medium">{selectedProject.location || 'Novi Pazar, Serbia'}</span>
                                    </li>
                                    <li className="flex justify-between border-b border-slate-200 pb-2">
                                        <span className="text-slate-500">Year</span>
                                        <span className="text-slate-900 font-medium">{selectedProject.year || '2024'}</span>
                                    </li>
                                    <li className="flex justify-between border-b border-slate-200 pb-2">
                                        <span className="text-slate-500">Client</span>
                                        <span className="text-slate-900 font-medium">{selectedProject.client || 'Private Investor'}</span>
                                    </li>
                                    <li className="flex justify-between pt-2">
                                        <span className="text-slate-500">Status</span>
                                        <span className="text-green-600 font-bold">{selectedProject.status || 'Completed'}</span>
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </div>
                </Modal>
            )}
        </Section>
    );
};

export default Portfolio;
